import { useDashboardsStore } from './dashboards-store';
import type { ComponentMetadata, DashboardTab, WidgetMetadata } from './dashboards-store.type';

/**
 * Migrates old string-based selectedTargets to new ComponentMetadata format
 * @param {WidgetMetadata} selectedTargets - The current selectedTargets from store
 * @returns {WidgetMetadata} Migrated metadata structure
 */
const migrateSelectedTargets = (selectedTargets: WidgetMetadata): WidgetMetadata => {
  const migrated: WidgetMetadata = {};

  Object.keys(selectedTargets).forEach((dashboardId) => {
    migrated[dashboardId] = {};
    const dashboard = selectedTargets[dashboardId];

    Object.keys(dashboard).forEach((componentId) => {
      const value = dashboard[componentId];

      // Check if it's already in new format (object with optional properties)
      if (typeof value === 'object' && value !== null) {
        migrated[dashboardId][componentId] = value as ComponentMetadata;
      } else if (typeof value === 'string') {
        // Migrate old string format to new object format
        migrated[dashboardId][componentId] = {
          selectedTargetId: value,
        };
      }
    });
  });

  return migrated;
};

/**
 * Sets the active dashboard tab in the Dashboards Store.
 *
 * @param {DashboardTab} tab - The dashboard tab to set ('dashboards' or 'templates')
 * @return {void}
 */
const setSelectedTab = (tab: DashboardTab): void => {
  useDashboardsStore.setState({ selectedTab: tab }, false, '[Dashboards Store] setSelectedTab');
};

/**
 * Gets component metadata for a specific component, with migration support
 *
 * @param {string} dashboardId - The ID of the dashboard
 * @param {string} componentId - The ID of the component
 * @return {ComponentMetadata | undefined} The component metadata, or undefined if not found
 */
const getComponentMetadata = (dashboardId: string, componentId: string): ComponentMetadata | undefined => {
  const currentState = useDashboardsStore.getState();
  const migratedTargets = migrateSelectedTargets(currentState.selectedTargets);
  return migratedTargets[dashboardId]?.[componentId];
};

/**
 * Updates component metadata for a specific component in a dashboard.
 * Merges with existing metadata.
 *
 * @param {string} dashboardId - The ID of the dashboard
 * @param {string} componentId - The ID of the component
 * @param {Partial<ComponentMetadata>} metadata - The metadata to update
 * @return {void}
 */
const updateComponentMetadata = (
  dashboardId: string,
  componentId: string,
  metadata: Partial<ComponentMetadata>,
): void => {
  const currentState = useDashboardsStore.getState();
  const migratedTargets = migrateSelectedTargets(currentState.selectedTargets);

  const existingMetadata = migratedTargets[dashboardId]?.[componentId] || {};
  const updatedMetadata = { ...existingMetadata, ...metadata };

  const updatedTargets = {
    ...migratedTargets,
    [dashboardId]: {
      ...migratedTargets[dashboardId],
      [componentId]: updatedMetadata,
    },
  };

  useDashboardsStore.setState({ selectedTargets: updatedTargets }, false, '[Dashboards Store] updateComponentMetadata');
};

/**
 * Sets the selected target for a specific component in a dashboard.
 * Legacy function that wraps updateComponentMetadata for backward compatibility.
 *
 * @param {string} dashboardId - The ID of the dashboard
 * @param {string} componentId - The ID of the component
 * @param {string} targetId - The ID of the target to select (single-select)
 * @return {void}
 */
const setTargetForComponent = (dashboardId: string, componentId: string, targetId: string): void => {
  updateComponentMetadata(dashboardId, componentId, { selectedTargetId: targetId });
};

/**
 * Clears the metadata for a specific component in a dashboard.
 *
 * @param {string} dashboardId - The ID of the dashboard
 * @param {string} componentId - The ID of the component whose metadata should be cleared
 * @return {void}
 */
const clearComponentMetadata = (dashboardId: string, componentId: string): void => {
  const currentState = useDashboardsStore.getState();
  const migratedTargets = migrateSelectedTargets(currentState.selectedTargets);
  const dashboardTargets = migratedTargets[dashboardId];

  if (!dashboardTargets) return;

  const updatedDashboardTargets = { ...dashboardTargets };
  delete updatedDashboardTargets[componentId];

  const updatedTargets = { ...migratedTargets };
  if (Object.keys(updatedDashboardTargets).length === 0) {
    delete updatedTargets[dashboardId];
  } else {
    updatedTargets[dashboardId] = updatedDashboardTargets;
  }

  useDashboardsStore.setState({ selectedTargets: updatedTargets }, false, '[Dashboards Store] clearComponentMetadata');
};

/**
 * Clears the selected target for a specific component in a dashboard.
 * Legacy function that wraps clearComponentMetadata for backward compatibility.
 *
 * @param {string} dashboardId - The ID of the dashboard
 * @param {string} componentId - The ID of the component whose target should be cleared
 * @return {void}
 */
const clearTargetForComponent = (dashboardId: string, componentId: string): void => {
  clearComponentMetadata(dashboardId, componentId);
};

export {
  clearTargetForComponent,
  getComponentMetadata,
  setSelectedTab,
  setTargetForComponent,
  updateComponentMetadata,
};
